import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Typography,
  Link,
  Box,
  alpha,
  useTheme,
} from '@mui/material';
import type { Components } from 'react-markdown';
import { brand, gray } from '../theme/themePrimitives';

interface MarkdownMessageProps {
  content: string;
  /** Whether this is a user message (affects color scheme) */
  isUserMessage?: boolean;
}

/**
 * MarkdownMessage - Renders markdown content with MUI-styled components
 *
 * Uses react-markdown which is XSS-safe by design (renders to React components,
 * not dangerouslySetInnerHTML). Raw HTML in markdown is NOT rendered.
 */
const MarkdownMessage: React.FC<MarkdownMessageProps> = ({
  content,
  isUserMessage = false
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Define custom component mappings for markdown elements
  const components: Components = {
    // Paragraphs
    p: ({ children }) => (
      <Typography
        component="p"
        variant="body2"
        sx={{
          lineHeight: 1.6,
          wordBreak: 'break-word',
          fontSize: { xs: '0.875rem', sm: '0.9rem' },
          letterSpacing: '0.01em',
          color: isUserMessage ? brand[50] : 'text.primary',
          mb: 1.5,
          '&:last-child': { mb: 0 },
        }}
      >
        {children}
      </Typography>
    ),

    // Headers - cap at h6 size to avoid huge text in chat
    h1: ({ children }) => (
      <Typography
        component="h6"
        variant="subtitle1"
        fontWeight={700}
        sx={{
          color: isUserMessage ? brand[50] : 'text.primary',
          mt: 2,
          mb: 1,
          '&:first-of-type': { mt: 0 },
        }}
      >
        {children}
      </Typography>
    ),
    h2: ({ children }) => (
      <Typography
        component="h6"
        variant="subtitle1"
        fontWeight={700}
        sx={{
          color: isUserMessage ? brand[50] : 'text.primary',
          mt: 2,
          mb: 1,
          '&:first-of-type': { mt: 0 },
        }}
      >
        {children}
      </Typography>
    ),
    h3: ({ children }) => (
      <Typography
        component="h6"
        variant="subtitle2"
        fontWeight={600}
        sx={{
          color: isUserMessage ? brand[50] : 'text.primary',
          mt: 1.5,
          mb: 0.75,
          '&:first-of-type': { mt: 0 },
        }}
      >
        {children}
      </Typography>
    ),
    h4: ({ children }) => (
      <Typography
        component="h6"
        variant="subtitle2"
        fontWeight={600}
        sx={{
          color: isUserMessage ? brand[50] : 'text.primary',
          mt: 1.5,
          mb: 0.75,
          '&:first-of-type': { mt: 0 },
        }}
      >
        {children}
      </Typography>
    ),
    h5: ({ children }) => (
      <Typography
        component="h6"
        variant="body2"
        fontWeight={600}
        sx={{
          color: isUserMessage ? brand[50] : 'text.primary',
          mt: 1,
          mb: 0.5,
          '&:first-of-type': { mt: 0 },
        }}
      >
        {children}
      </Typography>
    ),
    h6: ({ children }) => (
      <Typography
        component="h6"
        variant="body2"
        fontWeight={600}
        sx={{
          color: isUserMessage ? brand[50] : 'text.primary',
          mt: 1,
          mb: 0.5,
          '&:first-of-type': { mt: 0 },
        }}
      >
        {children}
      </Typography>
    ),

    // Bold text
    strong: ({ children }) => (
      <Typography
        component="strong"
        sx={{
          fontWeight: 600,
          color: isUserMessage ? brand[50] : 'text.primary',
        }}
      >
        {children}
      </Typography>
    ),

    // Italic text
    em: ({ children }) => (
      <Typography
        component="em"
        sx={{
          fontStyle: 'italic',
          color: isUserMessage ? brand[50] : 'text.primary',
        }}
      >
        {children}
      </Typography>
    ),

    // Inline code
    code: ({ children, className }) => {
      // Check if this is a code block (has language class) or inline code
      const isCodeBlock = className?.startsWith('language-');

      if (isCodeBlock) {
        return (
          <Box
            component="code"
            sx={{
              display: 'block',
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
            }}
          >
            {children}
          </Box>
        );
      }

      return (
        <Box
          component="code"
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.8rem',
            px: 0.75,
            py: 0.25,
            borderRadius: 1,
            bgcolor: isUserMessage
              ? alpha(brand[50], 0.2)
              : isDark
                ? alpha(gray[700], 0.6)
                : alpha(gray[200], 0.8),
            color: isUserMessage
              ? brand[50]
              : isDark
                ? gray[100]
                : gray[800],
          }}
        >
          {children}
        </Box>
      );
    },

    // Code blocks
    pre: ({ children }) => (
      <Box
        component="pre"
        sx={{
          fontFamily: 'monospace',
          fontSize: '0.8rem',
          lineHeight: 1.5,
          p: 1.5,
          my: 1.5,
          borderRadius: 2,
          bgcolor: isUserMessage
            ? alpha(brand[50], 0.15)
            : isDark
              ? alpha(gray[800], 0.8)
              : alpha(gray[100], 0.9),
          border: `1px solid ${
            isUserMessage
              ? alpha(brand[50], 0.2)
              : isDark
                ? alpha(gray[600], 0.3)
                : alpha(gray[300], 0.5)
          }`,
          overflowX: 'auto',
          maxWidth: '100%',
          '&::-webkit-scrollbar': {
            height: 6,
          },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: alpha(gray[400], 0.3),
            borderRadius: 3,
          },
        }}
      >
        {children}
      </Box>
    ),

    // Links - open in new tab with security attributes
    a: ({ href, children }) => (
      <Link
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          color: isUserMessage
            ? brand[100]
            : brand[isDark ? 300 : 500],
          textDecoration: 'underline',
          textDecorationColor: 'currentColor',
          textUnderlineOffset: '2px',
          '&:hover': {
            color: isUserMessage
              ? brand[50]
              : brand[isDark ? 200 : 600],
          },
        }}
      >
        {children}
      </Link>
    ),

    // Unordered lists
    ul: ({ children }) => (
      <Box
        component="ul"
        sx={{
          pl: 2.5,
          my: 1,
          listStyleType: 'disc',
          color: isUserMessage ? brand[50] : 'text.primary',
          '& li': {
            pl: 0.5,
            mb: 0.5,
            '&:last-child': { mb: 0 },
          },
        }}
      >
        {children}
      </Box>
    ),

    // Ordered lists
    ol: ({ children }) => (
      <Box
        component="ol"
        sx={{
          pl: 2.5,
          my: 1,
          listStyleType: 'decimal',
          color: isUserMessage ? brand[50] : 'text.primary',
          '& li': {
            pl: 0.5,
            mb: 0.5,
            '&:last-child': { mb: 0 },
          },
        }}
      >
        {children}
      </Box>
    ),

    // List items
    li: ({ children }) => (
      <Typography
        component="li"
        variant="body2"
        sx={{
          lineHeight: 1.6,
          fontSize: { xs: '0.875rem', sm: '0.9rem' },
          letterSpacing: '0.01em',
          color: isUserMessage ? brand[50] : 'text.primary',
        }}
      >
        {children}
      </Typography>
    ),

    // Blockquotes
    blockquote: ({ children }) => (
      <Box
        component="blockquote"
        sx={{
          borderLeft: `3px solid ${
            isUserMessage
              ? alpha(brand[50], 0.4)
              : brand[isDark ? 400 : 300]
          }`,
          pl: 2,
          ml: 0,
          my: 1.5,
          fontStyle: 'italic',
          color: isUserMessage
            ? alpha(brand[50], 0.9)
            : 'text.secondary',
        }}
      >
        {children}
      </Box>
    ),

    // Tables (GFM)
    table: ({ children }) => (
      <Box
        sx={{
          overflowX: 'auto',
          my: 1.5,
          '&::-webkit-scrollbar': {
            height: 6,
          },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: alpha(gray[400], 0.3),
            borderRadius: 3,
          },
        }}
      >
        <Box
          component="table"
          sx={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '0.8rem',
            '& th, & td': {
              border: `1px solid ${
                isUserMessage
                  ? alpha(brand[50], 0.25)
                  : isDark
                    ? alpha(gray[600], 0.4)
                    : alpha(gray[300], 0.6)
              }`,
              px: 1.5,
              py: 0.75,
              textAlign: 'left',
            },
            '& th': {
              bgcolor: isUserMessage
                ? alpha(brand[50], 0.1)
                : isDark
                  ? alpha(gray[700], 0.5)
                  : alpha(gray[100], 0.8),
              fontWeight: 600,
            },
            '& tr:nth-of-type(even) td': {
              bgcolor: isUserMessage
                ? alpha(brand[50], 0.05)
                : isDark
                  ? alpha(gray[800], 0.3)
                  : alpha(gray[50], 0.5),
            },
          }}
        >
          {children}
        </Box>
      </Box>
    ),

    // Table body (needed for proper table structure)
    tbody: ({ children }) => <tbody>{children}</tbody>,
    thead: ({ children }) => <thead>{children}</thead>,
    tr: ({ children }) => <tr>{children}</tr>,
    th: ({ children }) => (
      <th style={{ color: isUserMessage ? brand[50] : undefined }}>
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td style={{ color: isUserMessage ? brand[50] : undefined }}>
        {children}
      </td>
    ),

    // Horizontal rule
    hr: () => (
      <Box
        component="hr"
        sx={{
          border: 'none',
          borderTop: `1px solid ${
            isUserMessage
              ? alpha(brand[50], 0.3)
              : isDark
                ? alpha(gray[600], 0.4)
                : alpha(gray[300], 0.6)
          }`,
          my: 2,
        }}
      />
    ),
  };

  return (
    <Box
      sx={{
        textAlign: 'left',
        '& > *:first-of-type': { mt: 0 },
        '& > *:last-child': { mb: 0 },
      }}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    </Box>
  );
};

export default MarkdownMessage;
